import { useEffect } from "react";

type ThemeVariableName = "--ul-app-primary" | "--ul-app-font";

export type AppThemeVariables = Partial<Record<ThemeVariableName, string>>;

const THEME_VARIABLES: ThemeVariableName[] = ["--ul-app-primary", "--ul-app-font"];

export const useULAppTheme = (themeVariables: AppThemeVariables) => {
  const primaryColor = themeVariables["--ul-app-primary"];
  const fontColor = themeVariables["--ul-app-font"];

  useEffect(() => {
    const portalRoot = document.querySelector<HTMLElement>("#ul-modal");
    const targets = [document.body, portalRoot].filter((target): target is HTMLElement => !!target);

    if (targets.length === 0) {
      return;
    }

    const previousValues = new Map<HTMLElement, Record<ThemeVariableName, string>>();

    targets.forEach((target) => {
      previousValues.set(target, {
        "--ul-app-primary": target.style.getPropertyValue("--ul-app-primary"),
        "--ul-app-font": target.style.getPropertyValue("--ul-app-font"),
      });

      THEME_VARIABLES.forEach((variableName) => {
        const nextValue = variableName === "--ul-app-primary" ? primaryColor : fontColor;

        if (nextValue) {
          target.style.setProperty(variableName, nextValue);
          return;
        }

        target.style.removeProperty(variableName);
      });
    });

    return () => {
      targets.forEach((target) => {
        const previous = previousValues.get(target);

        if (!previous) {
          return;
        }

        THEME_VARIABLES.forEach((variableName) => {
          const currentValue = target.style.getPropertyValue(variableName).trim();
          const appliedValue =
            (variableName === "--ul-app-primary" ? primaryColor : fontColor)?.trim() ?? "";

          if (currentValue !== appliedValue) {
            return;
          }

          const previousValue = previous[variableName];

          if (previousValue) {
            target.style.setProperty(variableName, previousValue);
            return;
          }

          target.style.removeProperty(variableName);
        });
      });
    };
  }, [fontColor, primaryColor]);
};
